/**
 * Focus trap minimalista para el modal del SDK (R2, §11 plan — a11y).
 *
 * Sin dependencias. `activate()` guarda el elemento con foco previo (para
 * restaurarlo al desactivar), enfoca el primer elemento focuseable del
 * contenedor (o el contenedor mismo si no hay ninguno y tiene `tabindex=-1`),
 * y atrapa Tab/Shift+Tab ciclando entre el primero y el último elemento
 * focuseable visible. `deactivate()` limpia el listener y devuelve el foco.
 *
 * Selectores focuseables: `a[href]`, `button:not([disabled])`, `input`,
 * `select`, `textarea`, `[tabindex]:not([tabindex="-1"])`. Se filtran los
 * elementos ocultos (`display:none` / `visibility:hidden` / `disabled`).
 */

/** Selectores de elementos focuseables nativos + `tabindex`. */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export interface FocusTrap {
  /** Activa el trap: enfoca el primer focuseable y captura Tab. */
  activate(): void;
  /** Desactiva el trap: restaura el foco previo y limpia el listener. */
  deactivate(): void;
}

/**
 * Devuelve la lista de elementos focuseables visibles dentro de `container`
 * (en orden DOM). Un elemento se considera focuseable si matchea el selector,
 * no está oculto vía `display:none`/`visibility:hidden`, no tiene el atributo
 * `hidden` ni `aria-hidden="true"`, y no está `disabled`.
 *
 * Nota: no se usa `offsetParent`/`getClientRects()` porque jsdom (entorno de
 * tests) no implementa layout y esos valores son siempre nulos/vacíos. La
 * verificación por computed-style + atributos de ocultamiento es suficiente
 * para la superficie mínima del modal (botón de cierre + iframe).
 */
export function getFocusable(container: HTMLElement): HTMLElement[] {
  const nodes = Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  );
  return nodes.filter((el) => {
    if (el.hasAttribute('disabled')) return false;
    if (el.hasAttribute('hidden')) return false;
    if (el.getAttribute('aria-hidden') === 'true') return false;
    const style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    return true;
  });
}

/** Crea un focus trap sobre `container`. No activa hasta llamar `activate()`. */
export function createFocusTrap(container: HTMLElement): FocusTrap {
  let previousFocus: HTMLElement | null = null;
  let active = false;

  function handleKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Tab') return;
    const focusable = getFocusable(container);
    if (focusable.length === 0) {
      // Sin focuseables: impedir que Tab salga del contenedor.
      event.preventDefault();
      container.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const activeEl = document.activeElement;

    if (event.shiftKey) {
      // Shift+Tab: si el foco está en el primero (o fuera del contenedor),
      // saltar al último.
      if (activeEl === first || !container.contains(activeEl)) {
        event.preventDefault();
        last.focus();
      }
    } else {
      // Tab: si el foco está en el último (o fuera del contenedor), saltar
      // al primero.
      if (activeEl === last || !container.contains(activeEl)) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  function activate(): void {
    if (active) return;
    active = true;
    previousFocus = document.activeElement as HTMLElement | null;

    // Asegura que el contenedor mismo sea focuseable como respaldo.
    if (container.tabIndex < 0) {
      container.setAttribute('tabindex', '-1');
    }

    const focusable = getFocusable(container);
    if (focusable.length > 0) {
      focusable[0].focus();
    } else {
      container.focus();
    }

    document.addEventListener('keydown', handleKeydown);
  }

  function deactivate(): void {
    if (!active) return;
    active = false;
    document.removeEventListener('keydown', handleKeydown);
    if (previousFocus && typeof previousFocus.focus === 'function') {
      previousFocus.focus();
    }
    previousFocus = null;
  }

  return { activate, deactivate };
}